import {
  AdditionalReference, Packaging,
  PreviousDocument,
} from 'shared/ncts/common/types'
import { NctsCountry } from 'config'
import { FieldRequirement } from '../../../../../models/fieldRules'
import { ServerValidationResult } from '../../../../../models/types'

export type GetConsignmentItemFieldRequirementContext = {
  country?: string | null
  phase: 'p5' | 'p6'
  isOptIn: boolean
  security: string
  additionalDeclarationType: string
  specificCircumstanceIndicator: string
  valuesMap: Record<string, unknown>
  serverValidationResult?: ServerValidationResult
}

export type FieldRequirementResolverContext = {
  country?: string | null
  phase: 'p5' | 'p6'
  isOptIn: boolean
  security: string
  additionalDeclarationType: string
  specificCircumstanceIndicator?: string
  additionalReference?: AdditionalReference
  previousDocument?: PreviousDocument
  previousDocuments: PreviousDocument[]
  packaging?: Packaging
  dutyRateUnit: string
  consignmentItemDeclarationType?: string | null
  serverValidationResult?: ServerValidationResult
}

function stripPrefixes(serverValidationResult?: ServerValidationResult) {
  if (!serverValidationResult) return undefined

  const result: Record<string, FieldRequirement> = {}
  if (serverValidationResult.fieldRequirements) {
    for (const [k, v] of Object.entries(serverValidationResult.fieldRequirements)) {
      let key = k
      if (key.startsWith('consignmentItem.*.')) {
        key = key.replace('consignmentItem.*.', '')
      } else if (key.startsWith('houseConsignment.*.consignmentItem.*.')) {
        key = key.replace('houseConsignment.*.consignmentItem.*.', '')
      }
      result[key] = v
    }
  }

  return {
    ...serverValidationResult,
    fieldRequirements: result,
  }
}

export function toFieldRequirementResolverContext(path: string, {
  country,
  phase,
  isOptIn,
  security,
  additionalDeclarationType,
  specificCircumstanceIndicator,
  valuesMap,
  serverValidationResult,
}: GetConsignmentItemFieldRequirementContext): FieldRequirementResolverContext {
  const previousDocuments = valuesMap.previousDocument as PreviousDocument[] | undefined
  let previousDocument
  let additionalReference
  let packaging
  if (path.startsWith('previousDocument.')) {
    const parts = path.split('.')
    const index = parts[1]
    const valueKey = `previousDocument.${index}`
    previousDocument = valuesMap[valueKey] as PreviousDocument | undefined
  }

  if (path.startsWith('additionalReference.')) {
    const parts = path.split('.')
    const index = parts[1]
    const valueKey = `additionalReference.${index}`
    additionalReference = valuesMap[valueKey] as AdditionalReference | undefined
  }

  if (path.startsWith('packaging.')) {
    const parts = path.split('.')
    const index = parts[1]
    const valueKey = `packaging.${index}`
    packaging = valuesMap[valueKey] as Packaging | undefined
  }

  return {
    country: country ?? '' as NctsCountry,
    phase,
    isOptIn,
    security,
    specificCircumstanceIndicator,
    additionalDeclarationType,
    previousDocuments: previousDocuments?.filter((a): a is PreviousDocument => a !== undefined) ?? [],
    previousDocument,
    consignmentItemDeclarationType: valuesMap.declarationType as string,
    additionalReference,
    packaging,
    dutyRateUnit: valuesMap.dutyRateUnit as string,
    serverValidationResult: stripPrefixes(serverValidationResult),
  }
}
